import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconVolumeOff } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import VideoDropZone from '../../components/video/VideoDropZone';
import useVideoPaste from '../../components/video/useVideoPaste';
import { VideoFileRow } from '../../components/video/VideoFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    MAX_REMOVE_AUDIO_VIDEO_BYTES,
    formatFileSize,
    isSupportedVideoFile,
    removeAudioFromVideo,
    removeAudioVideoName,
} from './removeAudioVideoEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

function formatReductionPercent(originalSize, outputSize) {
    if (!originalSize || !outputSize || outputSize >= originalSize) return '0.0';
    return ((1 - outputSize / originalSize) * 100).toFixed(1);
}

export default function RemoveAudioVideo() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const videoPreviewUrl = useMemo(
        () => (file ? URL.createObjectURL(file) : ''),
        [file],
    );

    const resultPreviewUrl = useMemo(
        () => (result ? URL.createObjectURL(result.blob) : ''),
        [result],
    );

    useEffect(() => () => {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    }, [videoPreviewUrl]);

    useEffect(() => () => {
        if (resultPreviewUrl) URL.revokeObjectURL(resultPreviewUrl);
    }, [resultPreviewUrl]);

    const addFile = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const videos = list.filter(isSupportedVideoFile);
        if (!videos.length) {
            const msg = t('removeAudioVideo.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        const next = videos[0];
        if (next.size > MAX_REMOVE_AUDIO_VIDEO_BYTES) {
            const msg = t('removeAudioVideo.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(next);
        setResult(null);
        setError(null);
        setProgress(null);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page, the same as the image and PDF
    // tools. Ignored while a run is in flight: the clip would be dropped
    // on the floor by the reset that follows it.
    useVideoPaste(addFile, { enabled: !processing });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setProgress(null);
    };

    const resolveErrorMessage = (err) => {
        const code = err?.message;
        if (code === 'OUT_OF_MEMORY') return t('removeAudioVideo.errors.outOfMemory');
        if (code === 'ENCODE_FAILED' || code === 'EMPTY_OUTPUT' || code === 'REMOVE_AUDIO_FAILED') {
            return t('removeAudioVideo.errors.failed');
        }
        return t('removeAudioVideo.errors.failed');
    };

    const handleRemoveAudio = async () => {
        if (!file || processing) return;

        recordToolUsage('video/remove-audio', { category: 'video' });

        setProcessing(true);
        setResult(null);
        setError(null);
        setProgress(0);

        try {
            const blob = await removeAudioFromVideo(file, (value) => setProgress(value));
            const reductionPercent = formatReductionPercent(file.size, blob.size);
            const output = {
                name: removeAudioVideoName(file.name),
                originalSize: file.size,
                outputSize: blob.size,
                reductionPercent,
                blob,
            };
            setResult(output);
            notifications.show({
                title: t('common.success'),
                message: t('removeAudioVideo.successNotice', { percent: toBnDigits(reductionPercent) }),
                color: 'green',
            });
        } catch (err) {
            const msg = resolveErrorMessage(err);
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!file,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`removeAudioVideo.howTo.step${i}`)),
        [t],
    );

    const sizeNote = result ? (
        <>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {formatFileSize(result.originalSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">→</span>
            <span className="font-semibold text-[#1E9E5E]">
                {formatFileSize(result.outputSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">
                ({t('removeAudioVideo.reductionBadge', { percent: toBnDigits(result.reductionPercent) })})
            </span>
        </>
    ) : null;

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('removeAudioVideo.done'),
    });

    const resultDownloads = result ? [{
        id: result.name,
        name: result.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.outputSize),
        onDownload: () => downloadBlob(result.blob, result.name),
    }] : undefined;

    return (
        <ToolPageLayout
            slug="video/remove-audio"
            subtitleKey="removeAudioVideo.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={false}
            howToSteps={howToSteps}
            onReset={file || result || error ? handleReset : undefined}
            primaryAction={{
                label: t('removeAudioVideo.apply'),
                onClick: handleRemoveAudio,
                disabled: !file || processing || !!result,
                loading: processing,
                icon: <IconVolumeOff size={18} stroke={1.9} />,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />

                    {!file ? (
                        <VideoDropZone
                            title={t('removeAudioVideo.dropTitle')}
                            hint={t('removeAudioVideo.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <VideoFileRow
                            name={file.name}
                            meta={formatFileSize(file.size)}
                            onRemove={handleReset}
                            removeLabel={t('removeAudioVideo.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file ? (
                <ToolCard title={t('removeAudioVideo.infoTitle')}>
                    <p className="text-[13px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                        {t('removeAudioVideo.infoBody')}
                    </p>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('removeAudioVideo.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('removeAudioVideo.previewEmpty')}
                    idleHint={t('removeAudioVideo.previewEmptyHint')}
                    processingLabel={t('removeAudioVideo.processing')}
                    progress={progress ?? undefined}
                    error={error}
                    successLabel={t('removeAudioVideo.done')}
                    downloadLabel={t('removeAudioVideo.download')}
                    downloads={resultDownloads}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('removeAudioVideo.startOver')}
                    sizeNote={sizeNote}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('removeAudioVideo.apply')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                >
                    {file && !processing && !error ? (
                        <div className="flex h-full min-h-[280px] flex-col">
                            <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-black dark:border-zinc-700">
                                <video
                                    src={result ? resultPreviewUrl : videoPreviewUrl}
                                    controls
                                    className="max-h-[360px] w-full max-w-full"
                                />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <span className="truncate font-medium text-[#3D3833] dark:text-zinc-300">
                                    {result ? result.name : file.name}
                                </span>
                                <span>
                                    {result
                                        ? formatFileSize(result.outputSize)
                                        : t('removeAudioVideo.readyBadge')}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
